export const MEDIA_FILE_PICKER_TYPES = [
  {
    description: 'Media files',
    accept: {
      'video/*': ['.mp4', '.webm', '.mov', '.avi', '.mkv'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.aac'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
      'application/lottie+json': ['.json', '.lottie'],
    },
  },
] satisfies FilePickerAcceptType[]

export const MEDIA_FILE_INPUT_ACCEPT = Object.entries(MEDIA_FILE_PICKER_TYPES[0]?.accept ?? {})
  .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
  .join(',')

const FORMAT_LABEL_OVERRIDES: Record<string, string> = {
  webm: 'WebM',
  webp: 'WebP',
}

export function getSupportedMediaFormatLabels(): string[] {
  const extensions = Object.values(MEDIA_FILE_PICKER_TYPES[0]?.accept ?? {}).flat()
  return extensions.map((extension) => {
    const normalized = extension.replace(/^\./, '')
    return FORMAT_LABEL_OVERRIDES[normalized] ?? normalized.toUpperCase()
  })
}

export function hasMediaFilePickerSupport(): boolean {
  return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function'
}

export function createFileBackedMediaHandle(file: File): FileSystemFileHandle {
  const handle = {
    kind: 'file',
    name: file.name,
    getFile: async () => file,
    isSameEntry: async (other: FileSystemHandle) => other === handle,
    queryPermission: async () => 'granted' as PermissionState,
    requestPermission: async () => 'granted' as PermissionState,
  }

  return handle as FileSystemFileHandle
}

function createAbortError(): DOMException | Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('File selection was cancelled', 'AbortError')
  }
  const error = new Error('File selection was cancelled')
  error.name = 'AbortError'
  return error
}

async function showLegacyMediaFilePicker(options?: {
  multiple?: boolean
}): Promise<FileSystemFileHandle[]> {
  if (typeof document === 'undefined') {
    throw new Error('File picker not supported in this browser. Use Chrome or Edge.')
  }

  const files = await new Promise<File[]>((resolve, reject) => {
    const input = document.createElement('input')
    let settled = false

    const cleanup = () => {
      input.removeEventListener('change', handleChange)
      input.removeEventListener('cancel', handleCancel)
      input.remove()
    }

    const settle = (callback: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      callback()
    }

    const handleChange = () => {
      const selectedFiles = Array.from(input.files ?? [])
      settle(() => {
        if (selectedFiles.length === 0) {
          reject(createAbortError())
          return
        }
        resolve(selectedFiles)
      })
    }

    const handleCancel = () => {
      settle(() => reject(createAbortError()))
    }

    input.type = 'file'
    input.multiple = options?.multiple ?? true
    input.accept = MEDIA_FILE_INPUT_ACCEPT
    input.style.position = 'fixed'
    input.style.left = '-9999px'
    input.style.top = '0'
    input.setAttribute('aria-hidden', 'true')

    input.addEventListener('change', handleChange)
    input.addEventListener('cancel', handleCancel)
    document.body.append(input)
    input.click()
  })

  return files.map(createFileBackedMediaHandle)
}

export async function showMediaFilePicker(options?: {
  multiple?: boolean
  preferLegacyInput?: boolean
}): Promise<FileSystemFileHandle[]> {
  if (options?.preferLegacyInput) {
    return showLegacyMediaFilePicker(options)
  }

  if (hasMediaFilePickerSupport()) {
    return window.showOpenFilePicker({
      multiple: options?.multiple ?? true,
      types: MEDIA_FILE_PICKER_TYPES,
    })
  }

  return showLegacyMediaFilePicker(options)
}
